import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import {
  FilterParameters,
  PaginationQuery,
  generateCustomFilter,
  generateFilter,
  generateFilterExpression,
  generateIncludeQuery,
  generatePaginationQuery,
  generateSortQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { QueryOptions, SPARK_RESOURCE_TYPES } from 'constants/common';
import { useUserStore, useWorkspaceStore } from 'stores';
import { createJoinedMemories } from 'helpers/spark';
import type { MemoryFilterType } from 'components/Providers/ContextProviders/SparkSearchContextProviderV2';
import React from 'react';
import { MemoryResponse } from 'types/Memory';

const getFilter = (
  filters: MemoryFilterType,
  currentWorkspaceID: string,
  currentUserID: string
) => {
  const queryFilters: FilterParameters[] = [];
  const { search, category, selectedItemType } = filters || {};
  if (search) {
    queryFilters.push({
      key: 'name',
      value: search,
      type: 'contains',
    });
  }

  if (selectedItemType) {
    if (selectedItemType === 'workspaceSources')
      queryFilters.push({
        key: 'workspaceID',
        value: currentWorkspaceID,
        type: 'equal',
      });
    if (selectedItemType === 'mySources')
      queryFilters.push({
        key: 'createdBy',
        value: currentUserID,
        type: 'equal',
      });
  }

  const customFilters = [
    ...(category?.length
      ? [generateCustomFilter('category_name', category)]
      : []),
    ...(selectedItemType === 'myFavouritesSources'
      ? [generateCustomFilter('favourites', true)]
      : []),
  ];

  const defaultFilters = !queryFilters.length
    ? ''
    : queryFilters.length === 1
      ? generateFilter(
          queryFilters[0].key,
          queryFilters[0].value,
          queryFilters[0].type
        )
      : generateFilterExpression('and', queryFilters);

  return {
    custom: customFilters,
    default: defaultFilters,
  };
};

type MutationVariables = {
  filters?: MemoryFilterType;
  pagination?: PaginationQuery;
};

export function useMemorySearch(
  variables: MutationVariables,
  options?: QueryOptions
) {
  const currentWorkspaceID = useWorkspaceStore(
    (state) => state.currentWorkspaceID
  );
  const currentUserID = useUserStore(
    React.useCallback((state) => state.currentUserID, [])
  );

  const searchMemory = async ({ filters, pagination }: MutationVariables) => {
    if (!currentWorkspaceID) {
      return Promise.reject('currentWorkspaceID can not be null');
    }
    if (!currentUserID) {
      return Promise.reject('currentUserID can not be null');
    }

    const queryArray = [
      ...(filters
        ? [getFilter(filters, currentWorkspaceID, currentUserID).default]
        : []),
      generateSortQuery('modifiedAt', 'Decs'),
      generateIncludeQuery(['memoryFavourites']),
      ...(pagination ? [generatePaginationQuery(pagination)] : []),
    ];

    if (
      filters &&
      getFilter(filters, currentWorkspaceID, currentUserID).custom.length
    ) {
      queryArray.push(
        getFilter(filters, currentWorkspaceID, currentUserID).custom.join('&')
      );
    }
    const url = generateURL(SPARK_RESOURCE_TYPES.MEMORIES, queryArray);

    const response = await fetchWrapper<MemoryResponse>({
      url: url,
      method: 'GET',
    });
    return createJoinedMemories(response);
  };

  const enabled =
    Object.values(variables?.filters || {})?.some((filterItem) => {
      if (Array.isArray(filterItem)) {
        return !!filterItem.length;
      }
      return !!filterItem;
    }, []) || options?.enabled;

  return useQuery(
    ['search memories', variables],
    () => searchMemory(variables),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Sources: ${error.message}`);
        }
      },
      ...options,
      retry: 1,
      enabled,
      ...CACHE_AND_STALE_TIME,
    }
  );
}
